/* eslint-disable no-underscore-dangle */
import { graphqlClient } from 'bk-web-app';
import {
  CONFIRM_CATEGORY_MUTATION,
  RESOLVE_TASKS_MUTATION,
  GET_CONFIRM_CATEGORY_TASKS,
} from '../../queries';
import type {
  ConfirmCategoryResponse,
  ResolveTasksResponse,
  ConfirmCategoryMutationResponse,
  ResolveTasksMutationResponse,
  FailedDataEntry,
  FailedTask,
  InputError,
} from './types';

export interface DataEntryWithTask {
  id: string;
  amountInCents: number;
  task?: {
    id: string;
    status: string;
  };
  ledgerAccount?: { id: string };
}

export interface TaskOperationResult {
  success: boolean;
  data?: ConfirmCategoryResponse | ResolveTasksResponse;
  error?: string;
}

export interface TaskSummary {
  totalCount: number;
  selectedCount: number;
  skippedCount: number;
  taskIds: string[];
}
/**
 * Creates confirm category tasks for the given data entries
 */
export async function createConfirmCategoryTasks(
  dataEntryIds: string[],
  clientId: string,
): Promise<TaskOperationResult> {
  try {
    const { data } = await graphqlClient.mutate<ConfirmCategoryMutationResponse>({
      mutation: CONFIRM_CATEGORY_MUTATION,
      variables: {
        input: {
          clientId,
          dataEntries: dataEntryIds,
        },
      },
      context: { useV2: true },
      refetchQueries: [
        {
          query: GET_CONFIRM_CATEGORY_TASKS,
          variables: { clientId },
          context: { useV2: true },
        },
      ],
    });

    return processConfirmCategoryResponse(data?.confirmCategory, dataEntryIds);
  } catch (error) {
    return handleTaskOperationError(error, 'Failed to create tasks');
  }
}

/**
 * Cancels confirm category tasks with the given task IDs
 */
export async function cancelConfirmCategoryTasks(
  taskIds: string[],
  clientId: string,
): Promise<TaskOperationResult> {
  try {
    const { data } = await graphqlClient.mutate<ResolveTasksMutationResponse>({
      mutation: RESOLVE_TASKS_MUTATION,
      variables: {
        input: {
          clientId,
          taskIds,
          resolution: 'CANCELLED',
        },
      },
      context: { useV2: true },
      refetchQueries: [
        {
          query: GET_CONFIRM_CATEGORY_TASKS,
          variables: { clientId },
          context: { useV2: true },
        },
      ],
    });

    return processResolveTasksResponse(data?.resolveTasks);
  } catch (error) {
    return handleTaskOperationError(error, 'Failed to cancel tasks');
  }
}

export function getTaskOperationMessages(
  operation: 'create' | 'cancel',
  summary?: TaskSummary,
  result?: TaskOperationResult,
) {
  switch (operation) {
    case 'create':
      return getCreateTaskMessages(summary, result);
    case 'cancel':
      return getCancelTaskMessages(summary, result);
    default:
      throw new Error(`Unknown operation: ${operation}`);
  }
}

export function analyzeTaskCancellation(dataEntries: DataEntryWithTask[]): TaskSummary {
  const selectedCount = dataEntries.length;
  const taskIds = dataEntries
    .filter(entry => entry.task?.id && entry.task?.status === 'PENDING_WORK')
    .map(entry => entry.task!.id);

  const totalCount = taskIds.length;
  const skippedCount = selectedCount - totalCount;

  return {
    totalCount,
    selectedCount,
    skippedCount,
    taskIds,
  };
}

export function analyzeTaskCreation(dataEntries: DataEntryWithTask[]): TaskSummary {
  const selectedCount = dataEntries.length;
  const taskIds = dataEntries.filter(entry => !entry.task?.id).map(entry => entry.id);
  const totalCount = taskIds.length;
  const skippedCount = selectedCount - totalCount;

  return {
    totalCount,
    selectedCount,
    skippedCount,
    taskIds,
  };
}

export function validateTaskCreation(params: {
  taskType: string | null;
  dataEntriesCount: number;
  isFeatureEnabled: boolean;
}): { isValid: boolean; reason?: string } {
  if (!params.taskType) {
    return { isValid: false, reason: 'No task type selected' };
  }
  if (params.dataEntriesCount === 0) {
    return { isValid: false, reason: 'No data entries selected' };
  }
  if (!params.isFeatureEnabled) {
    return { isValid: false, reason: 'Feature not enabled' };
  }
  return { isValid: true };
}

export function validateTaskCancellation(params: {
  taskType: string | null;
  dataEntriesCount: number;
  isFeatureEnabled: boolean;
}): { isValid: boolean; reason?: string } {
  return validateTaskCreation(params);
}

function handleTaskOperationError(error: unknown, defaultMessage: string): TaskOperationResult {
  return {
    success: false,
    error: extractErrorMessage(error) || defaultMessage,
  };
}

function extractErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'An unexpected error occurred';
}

function formatInputErrors(inputErrors?: InputError[]): string {
  if (!inputErrors || inputErrors.length === 0) {
    return '';
  }
  return inputErrors.map((e: InputError) => `${e.field}: ${e.message}`).join(', ');
}

function formatFailureMessages(failures: FailedDataEntry[] | FailedTask[]): string {
  return failures
    .map(f => ('errorMessage' in f ? f.errorMessage : f.reason))
    .filter(Boolean)
    .join(', ');
}

function processConfirmCategoryResponse(
  response: ConfirmCategoryResponse | undefined,
  dataEntryIds: string[],
): TaskOperationResult {
  if (!response) {
    return {
      success: true,
      data: response,
    };
  }

  if (response.__typename === 'ConfirmCategoryComplete') {
    const hasFailures = response.failed && response.failed.length > 0;
    const successCount = response.success?.length || 0;
    const failedCount = response.failed?.length || 0;

    if (hasFailures && response.failed) {
      const failureMessages = formatFailureMessages(response.failed);

      return {
        success: successCount > 0,
        data: response,
        error: failureMessages || `Failed to create ${failedCount} task(s)`,
      };
    }

    return {
      success: successCount > 0,
      data: response,
    };
  }

  if (response.__typename === 'InvalidInputError') {
    const inputErrors = formatInputErrors(response.inputErrors);

    return {
      success: false,
      error: inputErrors || response.message || 'Invalid input',
    };
  }

  return {
    success: true,
    data: response,
  };
}

function processResolveTasksResponse(
  response: ResolveTasksResponse | undefined,
): TaskOperationResult {
  if (!response) {
    return {
      success: true,
      data: response,
    };
  }

  if (response.__typename === 'ResolveTasksComplete') {
    const hasFailures = response.failedTasks && response.failedTasks.length > 0;

    if (hasFailures && response.failedTasks) {
      const failedCount = response.failedTasks.length;
      const successCount = response.successTasks?.length || 0;
      const failureReasons = formatFailureMessages(response.failedTasks);

      return {
        success: successCount > 0,
        data: response,
        error: failureReasons || `Failed to cancel ${failedCount} task(s)`,
      };
    }

    return {
      success: true,
      data: response,
    };
  }

  if (response.__typename === 'InvalidInputError') {
    const inputErrors = formatInputErrors(response.inputErrors);

    return {
      success: false,
      error: inputErrors || response.message || 'Invalid input',
    };
  }

  return {
    success: true,
    data: response,
  };
}

function getCreateTaskMessages(summary?: TaskSummary, result?: TaskOperationResult) {
  const hasPartialSuccess =
    result?.data?.__typename === 'ConfirmCategoryComplete' &&
    result?.data?.failed &&
    result.data.failed.length > 0;

  const count = summary?.totalCount || 0;
  const skipped = summary?.skippedCount || 0;

  if (
    hasPartialSuccess &&
    result?.data?.__typename === 'ConfirmCategoryComplete' &&
    result.data.failed
  ) {
    const failedCount = result.data.failed.length;
    const successCount = result.data.success?.length || 0;

    return {
      success: {
        title: successCount > 0 ? 'Tasks partially created' : 'Failed to create tasks',
        message:
          successCount > 0
            ? `${successCount} task(s) created successfully, ${failedCount} failed.`
            : `All ${failedCount} task(s) failed to create.`,
      },
      error: {
        title: 'Failed to create tasks',
        message: result?.error || 'There was a problem creating tasks.',
      },
    };
  }

  return {
    success: {
      title: 'Tasks created successfully',
      message: 'Confirm Category tasks have been created.',
    },
    error: {
      title: 'Failed to create tasks',
      message: result?.error || 'There was a problem creating tasks.',
    },
    confirmation: {
      title: 'Confirm Tasks',
      message: formatCreationConfirmationMessage(count, skipped),
    },
  };
}

function getCancelTaskMessages(summary?: TaskSummary, result?: TaskOperationResult) {
  const count = summary?.totalCount || 0;
  const skipped = summary?.skippedCount || 0;

  const hasPartialSuccess =
    result?.data?.__typename === 'ResolveTasksComplete' &&
    result?.data?.failedTasks &&
    result.data.failedTasks.length > 0;

  if (
    hasPartialSuccess &&
    result?.data?.__typename === 'ResolveTasksComplete' &&
    result.data.failedTasks
  ) {
    const failedCount = result.data.failedTasks.length;
    const successCount = result.data.successTasks?.length || 0;

    return {
      success: {
        title: successCount > 0 ? 'Tasks partially cancelled' : 'Failed to cancel tasks',
        message:
          successCount > 0
            ? `${successCount} ${
                successCount === 1 ? 'task was' : 'tasks were'
              } cancelled successfully, ${failedCount} failed.`
            : `All ${failedCount} ${failedCount === 1 ? 'task' : 'tasks'} failed to cancel.`,
      },
      error: {
        title: 'Failed to cancel tasks',
        message: result?.error || 'There was a problem cancelling tasks.',
      },
      confirmation: {
        title: 'Cancel Confirm Category Tasks',
        message: formatCancellationConfirmationMessage(count, skipped),
      },
    };
  }

  const resolvedCount =
    (result?.data?.__typename === 'ResolveTasksComplete' ? result.data.successTasks?.length : 0) ||
    count;

  return {
    success: {
      title: 'Tasks cancelled successfully',
      message: `${resolvedCount} ${resolvedCount === 1 ? 'task was' : 'tasks were'} cancelled.`,
    },
    error: {
      title: 'Failed to cancel tasks',
      message: result?.error || 'There was a problem cancelling tasks.',
    },
    confirmation: {
      title: 'Cancel Confirm Category Tasks',
      message: formatCancellationConfirmationMessage(count, skipped),
    },
  };
}

function formatCancellationConfirmationMessage(count: number, skipped: number): string {
  const taskWord = count === 1 ? 'task' : 'tasks';
  const skipMessage =
    skipped > 0
      ? ` (${skipped} ${skipped === 1 ? 'entry' : 'entries'} without tasks will be skipped)`
      : '';

  return `You are about to cancel ${count} pending Confirm Category ${taskWord}${skipMessage}. This action cannot be undone.`;
}

function formatCreationConfirmationMessage(count: number, skipped: number): string {
  let message = `You are about to create ${count} Confirm Category ${
    count === 1 ? 'task' : 'tasks'
  }.`;
  if (skipped > 0) {
    message += ` ${skipped} ${skipped === 1 ? 'entry' : 'entries'} will be skipped because ${
      skipped === 1 ? 'it already has' : 'they already have'
    } a task.`;
  }
  message +=
    ' This will allow you to confirm the category of the selected transactions with the client.';
  return message;
}
